// Generated by scripts/generate-simple-client.mjs. Edit the JSON Schema source.
import { z } from "zod";

export type UnknownVariant = {
  kind: "unknown";
  originalKind: string;
  raw: Record<string, unknown>;
};
function openUnion<T>(
  variants: Record<string, z.ZodType<T>>,
): z.ZodType<T | UnknownVariant> {
  return z.unknown().transform((raw, ctx): T | UnknownVariant => {
    const tag = z
      .object({
        kind: z
          .string()
          .refine(
            (v) => Array.from(v).length >= 1 && Array.from(v).length <= 128,
          ),
      })
      .passthrough()
      .safeParse(raw);
    if (!tag.success) {
      ctx.addIssue({ code: "custom", message: "Expected an object with kind" });
      return z.NEVER;
    }
    const decoder = Object.hasOwn(variants, tag.data.kind)
      ? variants[tag.data.kind]
      : undefined;
    if (!decoder)
      return {
        kind: "unknown",
        originalKind: tag.data.kind,
        raw: structuredClone(tag.data),
      };
    const decoded = decoder.safeParse(raw);
    if (!decoded.success) {
      ctx.addIssue({
        code: "custom",
        message: `Malformed known variant: ${tag.data.kind}`,
      });
      return z.NEVER;
    }
    return decoded.data;
  });
}
export type Id = string;
export const IdSchema: z.ZodType<Id> = z
  .string()
  .refine(
    (v) => Array.from(v).length >= 1 && Array.from(v).length <= 256,
    "String length out of bounds",
  );
export type Timestamp = string;
export const TimestampSchema: z.ZodType<Timestamp> = z
  .string()
  .refine(
    (v) => Array.from(v).length >= 24 && Array.from(v).length <= 24,
    "String length out of bounds",
  )
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/);
export type ActivityState = "idle" | "working" | "waiting" | "unknown";
export const ActivityStateSchema: z.ZodType<ActivityState> = z.enum([
  "idle",
  "working",
  "waiting",
  "unknown",
]);
export type IssueCoverage =
  | "complete"
  | "partial"
  | "disabled"
  | "unsupported"
  | "unavailable";
export const IssueCoverageSchema: z.ZodType<IssueCoverage> = z.enum([
  "complete",
  "partial",
  "disabled",
  "unsupported",
  "unavailable",
]);
export type IssueRef = {
  id: Id;
  key: string;
  provider: string;
  kind: string;
  title: string | null;
  url: string | null;
};
export const IssueRefSchema: z.ZodType<IssueRef> = z.object({
  id: IdSchema,
  key: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 256,
      "String length out of bounds",
    ),
  provider: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 64,
      "String length out of bounds",
    ),
  kind: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 64,
      "String length out of bounds",
    ),
  title: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
      "String length out of bounds",
    )
    .nullable(),
  url: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 2048,
      "String length out of bounds",
    )
    .nullable(),
});
export type ProjectSummary = { id: Id; name: string };
export const ProjectSummarySchema: z.ZodType<ProjectSummary> = z.object({
  id: IdSchema,
  name: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
      "String length out of bounds",
    ),
});
export type SessionSummary = {
  id: Id;
  projectId: Id | null;
  title: string;
  provider: string;
  activity: ActivityState;
  preview: string | null;
  updatedAt: Timestamp | null;
  pendingRequestCount: number;
  issues: Array<IssueRef>;
  issueCoverage: IssueCoverage;
};
export const SessionSummarySchema: z.ZodType<SessionSummary> = z.object({
  id: IdSchema,
  projectId: IdSchema.nullable(),
  title: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
      "String length out of bounds",
    ),
  provider: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 64,
      "String length out of bounds",
    ),
  activity: ActivityStateSchema,
  preview: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 1024,
      "String length out of bounds",
    )
    .nullable(),
  updatedAt: TimestampSchema.nullable(),
  pendingRequestCount: z.number().int().min(0).max(1000000),
  issues: z.array(IssueRefSchema).min(0).max(16),
  issueCoverage: IssueCoverageSchema,
});
export type OverviewLimit = "sessions" | "projects" | "bytes" | "unavailable";
export const OverviewLimitSchema: z.ZodType<OverviewLimit> = z.enum([
  "sessions",
  "projects",
  "bytes",
  "unavailable",
]);
export type OverviewCoverage = {
  complete: boolean;
  limitedBy: Array<OverviewLimit>;
};
export const OverviewCoverageSchema: z.ZodType<OverviewCoverage> = z.object({
  complete: z.boolean(),
  limitedBy: z.array(OverviewLimitSchema).min(0).max(4),
});
export type SourceOverview = {
  kind: "overview";
  displayName: string;
  projects: Array<ProjectSummary>;
  sessions: Array<SessionSummary>;
  coverage: OverviewCoverage;
};
export const SourceOverviewSchema: z.ZodType<SourceOverview> = z.object({
  kind: z.literal("overview"),
  displayName: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
      "String length out of bounds",
    ),
  projects: z.array(ProjectSummarySchema).min(0).max(100),
  sessions: z.array(SessionSummarySchema).min(0).max(200),
  coverage: OverviewCoverageSchema,
});
export type TextFormat = "plain" | "markdown";
export const TextFormatSchema: z.ZodType<TextFormat> = z.enum([
  "plain",
  "markdown",
]);
export type TextContent = { kind: "text"; format: TextFormat; text: string };
export const TextContentSchema: z.ZodType<TextContent> = z.object({
  kind: z.literal("text"),
  format: TextFormatSchema,
  text: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 0 && Array.from(v).length <= 16000,
      "String length out of bounds",
    ),
});
export type ActivityContent = {
  kind: "activity";
  summary: string;
  toolCount: number;
  failedToolCount: number;
};
export const ActivityContentSchema: z.ZodType<ActivityContent> = z.object({
  kind: z.literal("activity"),
  summary: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
      "String length out of bounds",
    ),
  toolCount: z.number().int().min(0).max(1000000),
  failedToolCount: z.number().int().min(0).max(1000000),
});
export type FailureContent = {
  kind: "failure";
  toolName: string;
  message: string;
  exitCode: number | null;
};
export const FailureContentSchema: z.ZodType<FailureContent> = z.object({
  kind: z.literal("failure"),
  toolName: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 128,
      "String length out of bounds",
    ),
  message: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 2048,
      "String length out of bounds",
    ),
  exitCode: z.number().int().min(-2147483648).max(2147483647).nullable(),
});
export type MediaAvailability = "available" | "unavailable";
export const MediaAvailabilitySchema: z.ZodType<MediaAvailability> = z.enum([
  "available",
  "unavailable",
]);
export type MediaContent = {
  kind: "media";
  mediaId: Id;
  description: string;
  availability: MediaAvailability;
};
export const MediaContentSchema: z.ZodType<MediaContent> = z.object({
  kind: z.literal("media"),
  mediaId: IdSchema,
  description: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 0 && Array.from(v).length <= 512,
      "String length out of bounds",
    ),
  availability: MediaAvailabilitySchema,
});
export type Content =
  | TextContent
  | ActivityContent
  | FailureContent
  | MediaContent
  | UnknownVariant;
export const ContentSchema: z.ZodType<Content> = openUnion<
  Exclude<Content, UnknownVariant>
>({
  text: TextContentSchema,
  activity: ActivityContentSchema,
  failure: FailureContentSchema,
  media: MediaContentSchema,
});
export type MessageRole = "user" | "agent";
export const MessageRoleSchema: z.ZodType<MessageRole> = z.enum([
  "user",
  "agent",
]);
export type MessageState = "complete" | "growing" | "interrupted";
export const MessageStateSchema: z.ZodType<MessageState> = z.enum([
  "complete",
  "growing",
  "interrupted",
]);
export type ConversationMessage = {
  id: Id;
  role: MessageRole;
  createdAt: Timestamp | null;
  state: MessageState;
  truncated: boolean;
  content: Array<Content>;
};
export const ConversationMessageSchema: z.ZodType<ConversationMessage> =
  z.object({
    id: IdSchema,
    role: MessageRoleSchema,
    createdAt: TimestampSchema.nullable(),
    state: MessageStateSchema,
    truncated: z.boolean(),
    content: z.array(ContentSchema).min(1).max(64),
  });
export type QuestionRequest = {
  kind: "question";
  id: Id;
  prompt: string;
  options: Array<string>;
  canAnswer: false;
};
export const QuestionRequestSchema: z.ZodType<QuestionRequest> = z.object({
  kind: z.literal("question"),
  id: IdSchema,
  prompt: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 2048,
      "String length out of bounds",
    ),
  options: z
    .array(
      z
        .string()
        .refine(
          (v) => Array.from(v).length >= 1 && Array.from(v).length <= 512,
          "String length out of bounds",
        ),
    )
    .min(0)
    .max(20),
  canAnswer: z.literal(false),
});
export type PendingRequest = QuestionRequest | UnknownVariant;
export const PendingRequestSchema: z.ZodType<PendingRequest> = openUnion<
  Exclude<PendingRequest, UnknownVariant>
>({ question: QuestionRequestSchema });
export type Knowledge = "yes" | "no" | "unknown";
export const KnowledgeSchema: z.ZodType<Knowledge> = z.enum([
  "yes",
  "no",
  "unknown",
]);
export type HistoryLimit =
  | "maxMessages"
  | "compactionScope"
  | "bytes"
  | "unavailable";
export const HistoryLimitSchema: z.ZodType<HistoryLimit> = z.enum([
  "maxMessages",
  "compactionScope",
  "bytes",
  "unavailable",
]);
export type HistoryCoverage = {
  scope: "recentCompactions";
  compactions: 2;
  maxMessages: number;
  returnedMessages: number;
  earlierInScope: boolean;
  earlierOutsideScope: Knowledge;
  completeForRequest: boolean;
  limitedBy: Array<HistoryLimit>;
  anchorMessageId: Id | null;
};
export const HistoryCoverageSchema: z.ZodType<HistoryCoverage> = z.object({
  scope: z.literal("recentCompactions"),
  compactions: z.literal(2),
  maxMessages: z.number().int().min(1).max(100),
  returnedMessages: z.number().int().min(0).max(100),
  earlierInScope: z.boolean(),
  earlierOutsideScope: KnowledgeSchema,
  completeForRequest: z.boolean(),
  limitedBy: z.array(HistoryLimitSchema).min(0).max(4),
  anchorMessageId: IdSchema.nullable(),
});
export type Conversation = {
  kind: "conversation";
  sessionId: Id;
  activity: ActivityState;
  messages: Array<ConversationMessage>;
  pendingRequests: Array<PendingRequest>;
  coverage: HistoryCoverage;
};
export const ConversationSchema: z.ZodType<Conversation> = z.object({
  kind: z.literal("conversation"),
  sessionId: IdSchema,
  activity: ActivityStateSchema,
  messages: z.array(ConversationMessageSchema).min(0).max(100),
  pendingRequests: z.array(PendingRequestSchema).min(0).max(16),
  coverage: HistoryCoverageSchema,
});
export type ErrorCode =
  | "revisionMismatch"
  | "unavailable"
  | "notFound"
  | "anchorUnavailable"
  | "invalidRequest";
export const ErrorCodeSchema: z.ZodType<ErrorCode> = z.enum([
  "revisionMismatch",
  "unavailable",
  "notFound",
  "anchorUnavailable",
  "invalidRequest",
]);
export type ApiError = { kind: "error"; code: ErrorCode; message: string };
export const ApiErrorSchema: z.ZodType<ApiError> = z.object({
  kind: z.literal("error"),
  code: ErrorCodeSchema,
  message: z
    .string()
    .refine(
      (v) => Array.from(v).length >= 1 && Array.from(v).length <= 2048,
      "String length out of bounds",
    ),
});
export type SnapshotView =
  | SourceOverview
  | Conversation
  | ApiError
  | UnknownVariant;
export const SnapshotViewSchema: z.ZodType<SnapshotView> = openUnion<
  Exclude<SnapshotView, UnknownVariant>
>({
  overview: SourceOverviewSchema,
  conversation: ConversationSchema,
  error: ApiErrorSchema,
});
export type SnapshotEnvelope = {
  apiRevision: "simple-client-spike-1";
  subscriptionId: Id;
  sequence: number;
  view: SnapshotView;
};
export const SnapshotEnvelopeSchema: z.ZodType<SnapshotEnvelope> = z.object({
  apiRevision: z.literal("simple-client-spike-1"),
  subscriptionId: IdSchema,
  sequence: z.number().int().min(0).max(2147483647),
  view: SnapshotViewSchema,
});
export type ConversationQuery = {
  sessionId: Id;
  maxMessages: number;
  anchorMessageId: Id | null;
};
export const ConversationQuerySchema: z.ZodType<ConversationQuery> = z.object({
  sessionId: IdSchema,
  maxMessages: z.number().int().min(1).max(100),
  anchorMessageId: IdSchema.nullable(),
});
export const CONVERSATION_API_REVISION = "simple-client-spike-1";
export const MAX_MESSAGE_CONTENT_ITEMS = 64;
export const MAX_TEXT_LENGTH = 16000;
export const MAX_FAILURE_MESSAGE_LENGTH = 2048;
export const MAX_TOOL_NAME_LENGTH = 128;
export const MAX_MEDIA_DESCRIPTION_LENGTH = 512;
export const MAX_ID_LENGTH = 256;
export const MIN_FAILURE_EXIT_CODE = -2147483648;
export const MAX_FAILURE_EXIT_CODE = 2147483647;
export const MAX_UNKNOWN_KIND_LENGTH = 128;
export const MAX_SNAPSHOT_BYTES = 262144;
export function decodeSnapshot(encoded: string): SnapshotEnvelope {
  if (new TextEncoder().encode(encoded).length > MAX_SNAPSHOT_BYTES)
    throw new Error("Snapshot byte limit exceeded");
  return SnapshotEnvelopeSchema.parse(JSON.parse(encoded));
}
